/**
 * Deep-link / entry-point routing for the SPA.
 *
 * This isn't a full client router — the app is phase/state-driven — it just maps
 * the initial URL to an intent the home screen consumes once, then cleans the URL
 * back to "/". Recognised entry points:
 *   - /join/<CODE>            → jump into the join flow with the code prefilled
 *   - /join                   → join flow, code entry
 *   - /tv/<CODE>              → attach a display to a room (the TV view)
 *   - /tv                     → TV view, waiting to be told which room
 *   - /room?boxId&p&c         → NFC scan handoff (sun URL baked onto the chips)
 *   - /                       → home
 *   - anything else           → unknown (the app redirects it to "/")
 */
export type Route =
    | { kind: "home" }
    | { kind: "join"; code: string }
    | { kind: "joinPrompt" }
    | { kind: "nfc"; boxId: string; p: string; c: string }
    | { kind: "tv"; code: string | null }
    | { kind: "unknown" }

const sanitizeCode = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, "")

export function parseRoute(pathname: string, search: string): Route {
    const params = new URLSearchParams(search)

    // NFC scan (works on any path; the chips encode /room?boxId=...&p=...&c=...).
    const boxId = params.get("boxId")
    const p = params.get("p")
    const c = params.get("c")
    if (boxId && p && c) return { kind: "nfc", boxId, p, c }

    // Path-based routes.
    const path = pathname.replace(/\/+$/, "") || "/"
    if (path === "/") return { kind: "home" }
    if (path === "/join") return { kind: "joinPrompt" }

    // The TV view. Bare /tv is legitimate, not a mistake: a Cast receiver is
    // launched before anyone has told it which room to show, and a smart TV's
    // browser lands here so the code can be typed in with a remote.
    if (path === "/tv") return { kind: "tv", code: null }
    const tvMatch = path.match(/^\/tv\/([^/]+)$/)
    if (tvMatch) {
        const code = sanitizeCode(decodeURIComponent(tvMatch[1]))
        return { kind: "tv", code: code || null }
    }
    const joinMatch = path.match(/^\/join\/([^/]+)$/)
    if (joinMatch) {
        const code = sanitizeCode(decodeURIComponent(joinMatch[1]))
        return code ? { kind: "join", code } : { kind: "joinPrompt" }
    }

    return { kind: "unknown" }
}

/**
 * Paths the SPA itself handles, so a hard load or refresh gets the app shell.
 *
 * Lives here, beside [parseRoute], because it has to agree with it: the
 * production server (server.ts) redirects anything not on this list home, so a
 * route added to `parseRoute` and forgotten here becomes a 302 to "/" that looks
 * for all the world like a client-side bug. That is exactly how /tv shipped
 * broken. `spaRoutesCoverParseRoute` in route.test.ts now pins the two together.
 */
export function isSpaRoute(pathname: string): boolean {
    const path = pathname.replace(/\/+$/, "") || "/"
    return path === "/"
        || path === "/join" || path.startsWith("/join/")
        || path === "/tv" || path.startsWith("/tv/")
        || path === "/room"
}

/** Canonical shareable join URL for a room code, e.g. https://misquote.party/join/ABCD. */
export function joinUrl(origin: string, roomCode: string): string {
    return `${origin}/join/${roomCode}`
}

/**
 * Parse a URL read off an NFC tag (via Web NFC) into the same handoff params the
 * passive tag-tap flow uses. Guards on origin: a stray tag can hold any URL, and
 * we only act on our own SUN links (the server still validates the SDM
 * cryptographically, so this is defence-in-depth, not the only gate). Returns
 * null when the URL is malformed, cross-origin, or missing the SDM params.
 */
export function parseTagUrl(
    raw: string,
    expectedOrigin: string,
): { boxId: string; p: string; c: string } | null {
    let url: URL
    try {
        url = new URL(raw)
    } catch {
        return null
    }
    if (url.origin !== expectedOrigin) return null
    const route = parseRoute(url.pathname, url.search)
    return route.kind === "nfc" ? { boxId: route.boxId, p: route.p, c: route.c } : null
}
